import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiCallBegan } from './middleware/apiMiddleware';
import { API_BASE_URL } from '../utils/constants';

/**
 * LOCATION SLICE - Country, State, City Selection Management
 * Pattern: Follows middleware-based workflow with centralized API calls
 *
 * Features:
 * - Countries are fetched once and cached
 * - States are fetched on-demand when country is selected
 * - Cities are fetched on-demand when state is selected
 * - Selected location is persisted to localStorage
 */
const initialState = {
    // Country data (cached - fetched once)
    countries: [],
    countriesLoading: false,
    countriesError: null,
    countriesFetched: false, // Flag to prevent re-fetching

    // State data (fetched on country selection)
    states: [],
    statesLoading: false,
    statesError: null,

    // City data (fetched on state selection)
    cities: [],
    citiesLoading: false,
    citiesError: null,

    // Selected location
    selectedCountry: null,
    selectedState: null,
    selectedCity: null,

    // Modal visibility
    showLocationModal: false,

    // Auto-detect status flag (reserved for future UI, not currently shown)
    locationAutoDetecting: false,
};

const locationSlice = createSlice({
    name: 'location',
    initialState,
    reducers: {
        // ============ COUNTRY REDUCERS ============
        beginCountriesLoader: (state) => {
            state.countriesLoading = true;
            state.countriesError = null;
        },
        setCountriesData: (state, action) => {
            state.countriesLoading = false;
            // Handle different API response structures
            const payload = action.payload;
            if (Array.isArray(payload)) {
                state.countries = payload;
            } else if (payload?.data && Array.isArray(payload.data)) {
                state.countries = payload.data;
            } else {
                state.countries = [];
            }
            state.countriesError = null;
            state.countriesFetched = true;
        },
        countriesError: (state, action) => {
            state.countriesLoading = false;
            state.countriesError = action.payload?.errorMessage || 'Failed to fetch countries';
        },

        // ============ STATE REDUCERS ============
        beginStatesLoader: (state) => {
            state.statesLoading = true;
            state.statesError = null;
        },
        setStatesData: (state, action) => {
            state.statesLoading = false;
            // Handle different API response structures
            const payload = action.payload;
            if (Array.isArray(payload)) {
                state.states = payload;
            } else if (payload?.data && Array.isArray(payload.data)) {
                state.states = payload.data;
            } else {
                state.states = [];
            }
            state.statesError = null;
        },
        statesError: (state, action) => {
            state.statesLoading = false;
            state.statesError = action.payload?.errorMessage || 'Failed to fetch states';
        },
        clearStates: (state) => {
            state.states = [];
            state.selectedState = null;
            state.cities = [];
            state.selectedCity = null;
        },

        // ============ CITY REDUCERS ============
        beginCitiesLoader: (state) => {
            state.citiesLoading = true;
            state.citiesError = null;
        },
        setCitiesData: (state, action) => {
            state.citiesLoading = false;
            // Handle different API response structures
            const payload = action.payload;
            if (Array.isArray(payload)) {
                state.cities = payload;
            } else if (payload?.data && Array.isArray(payload.data)) {
                state.cities = payload.data;
            } else {
                state.cities = [];
            }
            state.citiesError = null;
        },
        citiesError: (state, action) => {
            state.citiesLoading = false;
            state.citiesError = action.payload?.errorMessage || 'Failed to fetch cities';
        },
        clearCities: (state) => {
            state.cities = [];
            state.selectedCity = null;
        },

        // ============ SELECTION REDUCERS ============
        setSelectedCountry: (state, action) => {
            state.selectedCountry = action.payload;
            // Clear states and cities when country changes
            state.states = [];
            state.selectedState = null;
            state.cities = [];
            state.selectedCity = null;
        },
        setSelectedState: (state, action) => {
            state.selectedState = action.payload;
            // Clear cities when state changes
            state.cities = [];
            state.selectedCity = null;
        },
        setSelectedCity: (state, action) => {
            state.selectedCity = action.payload;
        },

        // ============ MODAL REDUCERS ============
        showLocationModal: (state) => {
            state.showLocationModal = true;
        },
        hideLocationModal: (state) => {
            state.showLocationModal = false;
        },

        // ============ PERSISTENCE REDUCERS ============
        loadSavedLocation: (state) => {
            try {
                const savedLocation = localStorage.getItem('userLocation');
                if (savedLocation) {
                    const parsed = JSON.parse(savedLocation);
                    state.selectedCountry = parsed.country || null;
                    state.selectedState = parsed.state || null;
                    state.selectedCity = parsed.city || null;
                }
            } catch (error) {
                console.error('Error loading saved location:', error);
            }
        },
        saveLocation: (state) => {
            try {
                const locationData = {
                    country: state.selectedCountry,
                    state: state.selectedState,
                    city: state.selectedCity,
                };
                localStorage.setItem('userLocation', JSON.stringify(locationData));
            } catch (error) {
                console.error('Error saving location:', error);
            }
        },
        clearSavedLocation: (state) => {
            state.selectedCountry = null;
            state.selectedState = null;
            state.selectedCity = null;
            localStorage.removeItem('userLocation');
        },
        beginLocationAutoDetect: (state) => {
            state.locationAutoDetecting = true;
        },
        endLocationAutoDetect: (state) => {
            state.locationAutoDetecting = false;
        },
    },
});

// ============ EXPORT ACTIONS ============
export const {
    beginCountriesLoader,
    setCountriesData,
    countriesError,
    beginStatesLoader,
    setStatesData,
    statesError,
    clearStates,
    beginCitiesLoader,
    setCitiesData,
    citiesError,
    clearCities,
    setSelectedCountry,
    setSelectedState,
    setSelectedCity,
    showLocationModal,
    hideLocationModal,
    loadSavedLocation,
    saveLocation,
    clearSavedLocation,
    beginLocationAutoDetect,
    endLocationAutoDetect,
} = locationSlice.actions;

// ============ THUNK ACTIONS - Dispatch to middleware ============

/**
 * FETCH COUNTRIES ACTION
 * Fetches all countries (only if not already cached)
 */
export const fetchCountries = () => (dispatch, getState) => {
    const { countriesFetched } = getState().location;

    // Skip if already fetched (caching)
    if (countriesFetched) {
        return;
    }

    dispatch(
        apiCallBegan({
            url: '/countries',
            method: 'GET',
            onStart: beginCountriesLoader.type,
            onSuccess: setCountriesData.type,
            onError: countriesError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH STATES BY COUNTRY ACTION
 * Fetches states for a given country code
 *
 * @param {string} countryCode - Country code (e.g., "IN" for India)
 */
export const fetchStatesByCountry = (countryCode) => (dispatch) => {
    if (!countryCode) return;

    dispatch(
        apiCallBegan({
            url: `/state-list/${countryCode}`,
            method: 'GET',
            onStart: beginStatesLoader.type,
            onSuccess: setStatesData.type,
            onError: statesError.type,
            showGlobalError: false,
        })
    );
};

/**
 * FETCH CITIES BY STATE ACTION
 * Fetches cities for a given state ID
 *
 * @param {number} stateId - State ID
 */
export const fetchCitiesByState = (stateId) => (dispatch) => {
    if (!stateId) return;

    dispatch(
        apiCallBegan({
            url: `/cities/${stateId}`,
            method: 'GET',
            onStart: beginCitiesLoader.type,
            onSuccess: setCitiesData.type,
            onError: citiesError.type,
            showGlobalError: false,
        })
    );
};

/**
 * CONFIRM LOCATION SELECTION
 * Saves the selected location and closes modal
 */
export const confirmLocationSelection = () => (dispatch) => {
    dispatch(saveLocation());
    dispatch(hideLocationModal());
};

/**
 * AUTO DETECT AND SET LOCATION
 * Uses IP-based lookup (ipinfo.io) to infer country/state/city,
 * then resolves them to backend IDs and silently saves to Redux/localStorage.
 */
export const autoDetectAndSetLocation = () => async (dispatch, getState) => {
    try {
        dispatch(beginLocationAutoDetect());

        // If we already have a saved city, no need to auto-detect
        const { selectedCity } = getState().location;
        if (selectedCity) {
            dispatch(endLocationAutoDetect());
            return;
        }

        // 1. Get IP-based location data (city, region, country code)
        const ipResponse = await fetch('https://ipinfo.io/json');
        if (!ipResponse.ok) {
            dispatch(endLocationAutoDetect());
            return;
        }
        const ipData = await ipResponse.json();
        const ipCity = ipData?.city;
        const ipRegion = ipData?.region;
        const ipCountryCode = ipData?.country;

        if (!ipCountryCode) {
            dispatch(endLocationAutoDetect());
            return;
        }

        // Helper for case-insensitive comparison
        const normalize = (value) =>
            typeof value === 'string' ? value.trim().toLowerCase() : '';

        // 2. Get countries (reuse Redux cache if populated)
        let { countries, countriesFetched } = getState().location;
        if (!countriesFetched || !Array.isArray(countries) || countries.length === 0) {
            const countriesRes = await axios.get('/countries', { baseURL: API_BASE_URL });
            const data = countriesRes.data;
            countries = Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data)
                ? data
                : [];
        }

        const matchedCountry = (countries || []).find(
            (c) => normalize(c.code) === normalize(ipCountryCode)
        );
        if (!matchedCountry) {
            dispatch(endLocationAutoDetect());
            return;
        }

        // 3. Get states for matched country
        let states = [];
        try {
            const statesRes = await axios.get(`/state-list/${matchedCountry.code}`, {
                baseURL: API_BASE_URL,
            });
            const data = statesRes.data;
            states = Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data)
                ? data
                : [];
        } catch {
            dispatch(endLocationAutoDetect());
            return;
        }

        const matchedState = (states || []).find(
            (s) => ipRegion && normalize(s.name) === normalize(ipRegion)
        );
        if (!matchedState) {
            dispatch(endLocationAutoDetect());
            return;
        }

        // 4. Get cities for matched state
        let cities = [];
        try {
            const citiesRes = await axios.get(`/cities/${matchedState.id}`, {
                baseURL: API_BASE_URL,
            });
            const data = citiesRes.data;
            cities = Array.isArray(data?.data)
                ? data.data
                : Array.isArray(data)
                ? data
                : [];
        } catch {
            dispatch(endLocationAutoDetect());
            return;
        }

        const matchedCity = (cities || []).find(
            (c) => ipCity && normalize(c.name) === normalize(ipCity)
        );
        if (!matchedCity) {
            dispatch(endLocationAutoDetect());
            return;
        }

        // 5. Persist silently to Redux + localStorage
        dispatch(
            setSelectedCountry({
                id: matchedCountry.id,
                name: matchedCountry.name,
                code: matchedCountry.code,
            })
        );
        dispatch(
            setSelectedState({
                id: matchedState.id,
                name: matchedState.name,
            })
        );
        dispatch(
            setSelectedCity({
                id: matchedCity.id,
                name: matchedCity.name,
            })
        );
        dispatch(saveLocation());
    } catch (error) {
        // Fail silently for users; errors are non-fatal
        console.error('Auto-detect location failed:', error);
    } finally {
        dispatch(endLocationAutoDetect());
    }
};

// ============ SELECTORS ============

// Country selectors
export const selectCountries = (state) => state.location.countries;
export const selectCountriesLoading = (state) => state.location.countriesLoading;
export const selectCountriesError = (state) => state.location.countriesError;
export const selectCountriesFetched = (state) => state.location.countriesFetched;

// State selectors
export const selectStates = (state) => state.location.states;
export const selectStatesLoading = (state) => state.location.statesLoading;
export const selectStatesError = (state) => state.location.statesError;

// City selectors
export const selectCities = (state) => state.location.cities;
export const selectCitiesLoading = (state) => state.location.citiesLoading;
export const selectCitiesError = (state) => state.location.citiesError;

// Selected location selectors
export const selectSelectedCountry = (state) => state.location.selectedCountry;
export const selectSelectedState = (state) => state.location.selectedState;
export const selectSelectedCity = (state) => state.location.selectedCity;

// Modal selector
export const selectShowLocationModal = (state) => state.location.showLocationModal;

// Check if user has a saved location
export const selectHasSavedLocation = (state) => {
    const { selectedCity } = state.location;
    return selectedCity !== null;
};

// Get formatted location string - ALWAYS returns a string, never an object
export const selectFormattedLocation = (state) => {
    const { selectedCity, selectedState, selectedCountry } = state.location;
    
    // Helper to safely extract name from object or string
    const getName = (item) => {
        if (!item) return null;
        if (typeof item === 'string') return item;
        if (typeof item === 'object') return item.name || item.city_name || item.state_name || item.country_name || null;
        return null;
    };
    
    const cityName = getName(selectedCity);
    if (cityName) return cityName;
    
    const stateName = getName(selectedState);
    if (stateName) return stateName;
    
    const countryName = getName(selectedCountry);
    if (countryName) return countryName;
    
    return 'Select Location';
};

export default locationSlice.reducer;
